import type { ApprovalResponse } from '@common/types';
import type { FileDetails } from '@common/types/files';
import type { FormLockRequest, FormLockResponse } from '@common/types/formLocks';
import type {
    ContactRequestBody,
    DraftFormSubmissionResponse,
    FormSubmissionResponse,
    GetDraftFormSubmissionBody,
    GetFormSubmissionBody,
} from '@common/types/formSubmissions';
import type {
    CreateRetainer,
    RetainerContactAgreementResponse,
} from '@common/types/retainerAgreements';
import type {
    QuoteDetails,
    TemplateFormCodeResult,
    TemplateFormResponse,
} from '@common/types/templateForms';
import type {
    DraftFormSubmissionRequest,
    FormSubmissionRequest,
    QuoteFormSubmission,
    SilentDraftFormSubmissionRequest,
} from '@common/types/workflows';
import axiosInstance from '@/services/axiosInstance';

export async function getClientSideQuoteForms(quoteTemplateFormId: string) {
    return axiosInstance.get<TemplateFormResponse>(
        `api/v1/template-forms/${quoteTemplateFormId}/quote-form`,
    );
}

export async function getClientSideIntakeForms(
    intakeTemplateFormId: string,
    quoteTemplateId?: number,
) {
    return axiosInstance.get<TemplateFormResponse>(
        `api/v1/template-forms/${intakeTemplateFormId}/intake-form`,
        { params: { quoteTemplateId } },
    );
}

export async function getTemplateDataFromCode(quoteSixDigitCode: string) {
    return axiosInstance.get<QuoteDetails>(
        `api/v1/template-forms/${quoteSixDigitCode}/quote-details`,
    );
}

export async function getTemplateDataFromTemplateFormId(templateFormId: string) {
    return axiosInstance.get<TemplateFormCodeResult>(
        `api/v1/template-forms/${templateFormId}/template-form-code`,
    );
}

export async function clientSideFormSubmit(payload: QuoteFormSubmission) {
    return axiosInstance.post<ApprovalResponse>('/api/v1/quote-form:submit', payload);
}

export async function clientSideOrchestrationFormSubmit(
    instanceId: string,
    prospectId: string,
    payload: FormSubmissionRequest,
) {
    return axiosInstance.post<ApprovalResponse>(
        `/api/v1/intakeForm/${instanceId}/${prospectId}`,
        payload,
    );
}

export async function clientSideIntakeOnlyFormSubmit(payload: FormSubmissionRequest) {
    return axiosInstance.post<ApprovalResponse>('api/v1/intakeOnly', payload);
}

export async function clientSideDraftIntakeFormSubmit(payload: DraftFormSubmissionRequest) {
    return axiosInstance.post<ApprovalResponse & { DraftFormSubmissionId: string }>(
        'api/v1/draftIntakeFormSubmission',
        payload,
    );
}

export async function clientSideSilentDraftIntakeFormSave(
    payload: SilentDraftFormSubmissionRequest,
) {
    return axiosInstance.post<ApprovalResponse & { DraftFormSubmissionId: string | null }>(
        'api/v1/draftIntakeFormSubmission/silent',
        payload,
    );
}

export async function verifyClientForDraftForm(payload: GetDraftFormSubmissionBody) {
    return axiosInstance.post<DraftFormSubmissionResponse>(
        'api/v1/forms/formSubmissionAsDraft',
        payload,
    );
}

export async function getClientPostSubmittedIntakeFormData(payload: GetFormSubmissionBody) {
    return axiosInstance.post<FormSubmissionResponse>('api/v1/forms/formSubmission', payload);
}

export async function clientSidePostIntakeSubmission(payload: FormSubmissionRequest) {
    return axiosInstance.post<ApprovalResponse>('api/v1/postIntakeFormSubmission', payload);
}

export async function getIntakeOnlyFormStatus(draftFormSubmissionId: string) {
    return axiosInstance.get<ApprovalResponse>(`api/v1/intakeOnly/${draftFormSubmissionId}/status`);
}

export async function getPostIntakeFormStatus(formSubmissionId: string) {
    return axiosInstance.get<ApprovalResponse>(`api/v1/intakeForm/${formSubmissionId}/status`);
}

export async function getFormLockStatus(
    contactId: string,
    prospectId?: string | null,
    prospectVersionId?: number | null,
) {
    return axiosInstance.get<FormLockResponse>(`api/v1/form-locks/${contactId}`, {
        params: {
            prospectId: prospectId || undefined,
            prospectVersionId: prospectVersionId || undefined,
        },
    });
}

export async function postContactIdForLocking(payload: ContactRequestBody) {
    return axiosInstance.post<string>('api/v1/forms/contact-id', payload);
}

export async function unlockFormStatus(payload: FormLockRequest) {
    return axiosInstance.post<string>('api/v1/form-locks/unlock', payload);
}

/**
 * Best-effort form unlock for page teardown events like `beforeunload`, `pagehide`,
 * and mobile `visibilitychange`.
 *
 * This intentionally avoids axios/auth token work because teardown events have very
 * little time to finish. Failures are swallowed because this is best-effort cleanup.
 */
export function unlockFormStatusQuickly(payload: { contactId: string; prospectId: string }) {
    const url = `${import.meta.env.VITE_APP_API_URL}/api/v1/form-locks/unlock`;
    const body = JSON.stringify(payload);

    if (navigator.sendBeacon) {
        const sent = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));

        if (sent) return;
    }

    void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
    }).catch(() => {
        // Best-effort unlock during page teardown.
    });
}

export async function extendFormLockStatus(payload: FormLockRequest) {
    return axiosInstance.post<string>('api/v1/form-locks/extend', payload);
}

export async function getRetainerDetailsUsingProspectId(prospectId: string) {
    return axiosInstance.get<RetainerContactAgreementResponse[]>(
        `api/v1/retainer-agreement/${prospectId}`,
    );
}

export async function createRetainerAgreementFillUp(payload: CreateRetainer) {
    return axiosInstance.post<FileDetails & { clientName: string }>(
        'api/v1/retainer-agreement/retainer-fillup',
        payload,
    );
}
